// A learned fix that belongs to an ENDPOINT + model rather than to a model alone, of which there are now
// two (`seedFix`, `systemFix`) and will be more: an OpenAI-compatible server refuses some field, the
// inference layer retries once without it, and the answer is remembered so the next request is a single
// call. Everything about that is the same every time except the settings key and the sentence the user is
// shown, so those are the only two things a store here takes.
//
// Why keyed per endpoint and not per model: strictness lives in whoever renders the template or forwards
// the field, not in the model. The same `qwen3.5-4b` refuses the multi-system shape on a local LM Studio
// and takes it through NanoGPT, and NanoGPT forwards `seed` only on some routes. A fix learned on one must
// not pre-change the other. (Contrast `reasoningFix`, keyed per model: which reasoning tiers exist is
// model-intrinsic, and it has its own scoping and migration, so it does NOT use this.)
import { forgetSettingsCache, loadJsonSetting, saveJsonSetting } from "./api";
import { REASONING_FIX_EVENT } from "./reasoningFix";

/** What is stored per key. `host` is kept so the Learned fixes list can name the server without
 *  re-parsing, and so a renamed provider row still reads sensibly. */
export interface EndpointFix { model: string; host: string }

/** One key per (endpoint, model). The base URL rather than the provider row id, so deleting and re-adding
 *  the same server does not forget what it refuses. */
export const endpointFixKey = (baseUrl: string, model: string): string =>
  `${(baseUrl || "").trim().replace(/\/+$/, "").toLowerCase()}|${model}`;

const hostOf = (key: string): string => {
  const base = key.split("|")[0];
  try { return new URL(base).host; } catch { return base; }
};

export interface EndpointFixStore {
  /** Read the saved fixes into memory. Called once at startup. */
  load(): Promise<void>;
  /** Does this endpoint+model have the fix applied? */
  has(key: string): boolean;
  /** Record the fix and announce it ONCE. Re-recording a key already held is silent, otherwise every
   *  utility call on that route would re-announce it. */
  note(key: string, model: string): void;
  /** Everything learned, for the Learned fixes list in Settings. */
  all(): Array<{ key: string } & EndpointFix>;
  /** Forget one. The next request sends the normal shape again and re-learns if it is still refused, at
   *  the cost of one retried call. */
  clear(key: string): void;
  /** Testing seam. */
  reset(): void;
}

/**
 * A learned-fix store over one settings key.
 *
 * Held in memory as well as on disk because `has` is consulted while BUILDING every request, which cannot
 * await a settings read. `sentence` produces the toast text; the detail of `REASONING_FIX_EVENT` is shown
 * verbatim, so it is a whole sentence ending in where to review it.
 */
export function endpointFixStore(
  settingsKey: string,
  sentence: (model: string, host: string) => string,
): EndpointFixStore {
  let fixes: Record<string, EndpointFix> = {};
  // Entries from the first build of either feature were keyed by bare model id with `true` values.
  // Dropped rather than migrated: there is no endpoint to attribute them to, and re-learning costs
  // one retried request.
  const read = async () => Object.fromEntries(
    Object.entries(await loadJsonSetting<Record<string, unknown>>(settingsKey, {}))
      .filter(([k, v]) => k.includes("|") && !!v && typeof v === "object"),
  ) as Record<string, EndpointFix>;
  // A write edits what is saved NOW, not this window's copy: every window holds its own, and one that
  // loaded before another learned something wrote its snapshot back over it. Chained, so two quick
  // writes in one window do not race each other either.
  let writes = Promise.resolve();
  const persist = (edit: (m: Record<string, EndpointFix>) => void) => {
    // The settings read is cached per window, which is exactly the stale copy this avoids: drop it first.
    writes = writes.then(async () => { forgetSettingsCache(settingsKey); const m = await read(); edit(m); await saveJsonSetting(settingsKey, m); }).catch(() => {});
  };

  return {
    async load() {
      fixes = await read();
    },

    has: (key) => key in fixes,

    note(key, model) {
      if (!key || key in fixes) return;
      const host = hostOf(key);
      fixes = { ...fixes, [key]: { model, host } };
      persist((m) => { m[key] = { model, host }; });
      window.dispatchEvent(new CustomEvent(REASONING_FIX_EVENT, { detail: sentence(model, host) }));
    },

    all: () => Object.entries(fixes).map(([key, v]) => ({ key, ...v })),

    clear(key) {
      if (!(key in fixes)) return;
      const next = { ...fixes };
      delete next[key];
      fixes = next;
      persist((m) => { delete m[key]; });
    },

    reset() {
      fixes = {};
    },
  };
}
